import React from 'react';
import { getRenderCardOverrides } from '../../utils';
import dynamic from 'next/dynamic';
import { useProductCardElements } from '../../../ProductListWrapper/utils';
import { useListPresentationContextData } from '../../context/ListPresentationContext';
import useHandleClicks from '../../hooks/useHandleClicks';
import { RenderCardSkeleton } from './SkeletonRenderer';
import { useLastElementObservable } from './useLastElementObservable';

const FeatureProductCards = dynamic(() =>
  import('./FeatureProductCards').then((mod) => mod.FeatureProductCards)
);
const CardFrameForCollection = dynamic(() =>
  import('../../../CardFrameCollection').then((mod) => mod.default)
);

export default function ProductCardRenderer() {
  const data = useListPresentationContextData();
  const { isMobile, listPresentationConfig, cardFrameConfig, contentTypeConditions } =
    data.state;
  const { getConfigsAndUtils } = data.utilities;
  const { oneGridCellGeometry, sectionColors, containerTracks } =
    getConfigsAndUtils().generalValues;
  const { onListItemClick } = useHandleClicks();
  const { isProductCardEnabled } = useProductCardElements();
  const [lastItemRef, showAllItemsRendered, listToRender, realCountToShow] =
    useLastElementObservable();

  if (!listToRender?.length) {
    return <RenderCardSkeleton numberOfSkeletons={containerTracks.numColumns} />;
  }

  return (
    <>
      {listToRender.map((data, index, itemsRendered) => {
        const showLastItemRef =
          index === itemsRendered?.length - 1 && !showAllItemsRendered;
        const ObservableElement = showLastItemRef ? <div ref={lastItemRef} /> : null;
        if (
          contentTypeConditions.featuredCollection ||
          contentTypeConditions.featuredTag ||
          contentTypeConditions.featuredCategory
        ) {
          const textColorOverride = getRenderCardOverrides({
            listPresentationConfig,
            sectionColors,
          });
          return (
            <>
              <FeatureProductCards
                key={index}
                isProductCardEnabled={isProductCardEnabled}
                data={data}
                index={index}
                oneGridCellGeometry={oneGridCellGeometry}
                isMobile={isMobile}
                sectionColorsOverrides={{ textColor: textColorOverride }}
                customClass={'!tw-h-auto !tw-w-min'}
              />
              {ObservableElement}
            </>
          );
        }
        return (
          <>
            <CardFrameForCollection
              key={index}
              itemConfig={data}
              itemIndex={index}
              containerTracks={containerTracks}
              cardFrameConfig={cardFrameConfig}
              oneGridCellGeometry={oneGridCellGeometry}
              openInNewTab={listPresentationConfig?.openInNewTab}
              isMobile={isMobile}
              onCollectionClick={onListItemClick}
              contentTypeConditions={contentTypeConditions}
            />
            {ObservableElement}
          </>
        );
      })}
      {!showAllItemsRendered && (
        <RenderCardSkeleton numberOfSkeletons={realCountToShow - listToRender?.length} />
      )}
    </>
  );
}
